<template>
  <vue-final-modal :model-value="showConsentModal" @update:model-value="updateShowConsentModal"
    classes="modal-container" content-class="modal-content consent-modal-content">
    <button class="modal__close" @click="updateShowConsentModal(false)">
      <i class="far fa-times-circle"></i>
    </button>
    <div class="modal__content">
      <h2 class="consent-title">Procedure Consent Form</h2>

      <div class="consent-form">

        <div class="form-section template-section">
          <h3 class="section-title">Consent Template</h3>
          <div class="form-row">
            <div class="form-group full-width">
              <label for="templateSelector">Select Procedure Template:</label>
              <select id="templateSelector" v-model="selectedTemplateKey" @change="applySelectedTemplate"
                class="form-input">
                <option v-for="template in availableTemplates" :key="template" :value="template">
                  {{ template }}
                </option>
              </select>
              <small class="helper-text">
                * Switching templates will reset the Procedure Name, Benefits, Risks, and Alternatives below.
              </small>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">Basic Information</h3>

          <div class="form-row">
            <div class="form-group">
              <label>Primary Classification:</label>
              <input type="text" :value="consentForm.primaryClassification" readonly
                class="form-input readonly-input" />
            </div>
            <div class="form-group" v-if="consentForm.secondaryClassification">
              <label>Secondary Classification:</label>
              <input type="text" :value="consentForm.secondaryClassification" readonly
                class="form-input readonly-input" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="consentPatientName">Patient Name:</label>
              <input id="consentPatientName" type="text" :value="consentForm.patientName"
                @input="updateConsentField('patientName', $event.target.value)" class="form-input"
                placeholder="Enter patient name" />
            </div>

            <div class="form-group">
              <label for="consentPatientEmail">Patient Email:</label>
              <input id="consentPatientEmail" type="email" :value="consentForm.patientEmail"
                @input="updateConsentField('patientEmail', $event.target.value)" class="form-input"
                placeholder="Enter patient email" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="consentProcedure">Procedure:</label>
              <input id="consentProcedure" type="text" :value="consentForm.procedure"
                @input="updateConsentField('procedure', $event.target.value)" class="form-input"
                placeholder="Enter procedure" />
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">Clinical Details</h3>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="consentToothNumber">Tooth or teeth to be treated:</label>
              <input id="consentToothNumber" type="text" :value="consentForm.toothNumber"
                @input="updateConsentField('toothNumber', $event.target.value)" class="form-input"
                placeholder="Enter tooth number(s)" />
            </div>
          </div>

          <div class="form-group full-width">
            <label for="briefExplanation">Brief explanation of medical term if not clear:</label>
            <textarea id="briefExplanation" :value="consentForm.briefExplanation"
              @input="updateConsentField('briefExplanation', $event.target.value)" class="form-textarea"
              placeholder="Brief explanation of medical term if not clear" rows="3"></textarea>
          </div>

          <div class="form-group full-width">
            <label for="consentIntendedBenefits">The intended benefits:</label>
            <textarea id="consentIntendedBenefits" :value="consentForm.intendedBenefits"
              @input="updateConsentField('intendedBenefits', $event.target.value)" class="form-textarea"
              placeholder="Enter the intended benefits of the procedure" rows="3"></textarea>
          </div>

          <div class="form-group full-width">
            <label for="consentComplexities">The following challenges or complexities have been identified for your
              case:</label>
            <textarea id="consentComplexities" :value="consentForm.complexities"
              @input="updateConsentField('complexities', $event.target.value)" class="form-textarea"
              placeholder="Enter challenges or complexities" rows="3"></textarea>
          </div>

          <div class="form-group full-width">
            <label for="consentSpecificRisks">These complexities bring the following specific risks:</label>
            <textarea id="consentSpecificRisks" :value="consentForm.specificRisks"
              @input="updateConsentField('specificRisks', $event.target.value)" class="form-textarea"
              placeholder="Enter specific risks related to complexities" rows="3"></textarea>
          </div>

          <div class="form-group full-width">
            <label for="consentSignificantRisks">Significant, unavoidable or frequently occurring risks:</label>
            <textarea id="consentSignificantRisks" :value="consentForm.significantRisks"
              @input="updateConsentField('significantRisks', $event.target.value)" class="form-textarea"
              placeholder="Enter significant, unavoidable or frequently occurring risks" rows="4"></textarea>
          </div>

          <div class="form-group full-width">
            <label for="consentAlternatives">Alternative treatment options:</label>
            <textarea id="consentAlternatives" :value="consentForm.alternatives"
              @input="updateConsentField('alternatives', $event.target.value)" class="form-textarea"
              placeholder="Enter alternative treatment options" rows="3"></textarea>
          </div>
        </div>
      </div>

      <div class="consent-actions">
        <button class="btn-primary" @click="printConsentForm">
          <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="currentColor"
              d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" />
          </svg>
          Print
        </button>
        <button class="btn-secondary" @click="emailConsentForm">
          <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="currentColor"
              d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          Email
        </button>
      </div>
    </div>
  </vue-final-modal>

  <vue-final-modal :model-value="showEmailModal" @update:model-value="updateShowEmailModal" classes="modal-container"
    content-class="modal-content email-modal-content">
    <button class="modal__close" @click="updateShowEmailModal(false)">
      <i class="far fa-times-circle"></i>
    </button>
    <div class="modal__content">
      <h3>Send Consent Form</h3>
      <p>Please confirm or modify the email address to send the consent form:</p>

      <div class="email-form-group">
        <label for="sendToEmail">Recipient Email:</label>
        <input id="sendToEmail" type="email" :value="emailToSend" @input="updateEmailToSend($event.target.value)"
          class="form-input" placeholder="Enter email address" />
      </div>

      <div class="email-actions">
        <button class="btn-secondary" @click="updateShowEmailModal(false)">
          <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="currentColor"
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
          Cancel
        </button>
        <button class="btn-primary" @click="confirmSendEmail">
          <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
          Send
        </button>
      </div>
    </div>
  </vue-final-modal>
</template>

<script>
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import authStore from "../../store/auth-store";
import { COMPLEXITY_RISKS_MAP, PROCEDURETEMPLATES } from './ConsentData';

export default {
  name: 'ConsentFormModals',
  props: {
    showConsentModal: { type: Boolean, default: false },
    showEmailModal: { type: Boolean, default: false },
    consentForm: { type: Object, required: true },
    currentRowSystemType: { type: Number, default: 0 },
    emailToSend: { type: String, default: '' }
  },
  emits: [
    'update:showConsentModal',
    'update:showEmailModal',
    'update:consentForm',
    'update:emailToSend',
    'print',
    'email',
    'confirmSend'
  ],

  data() {
    return {
      survey: null,
      currentSurveyId: null,
      selectedTemplateKey: 'Endodontic Treatment',
      availableTemplates: Object.keys(PROCEDURETEMPLATES),
      isLoading: false
    }
  },

  computed: {
    user() {
      return authStore.state.user;
    }
  },

  methods: {
    updateShowConsentModal(value) {
      this.$emit('update:showConsentModal', value)
    },

    updateShowEmailModal(value) {
      this.$emit('update:showEmailModal', value)
    },

    updateConsentField(fieldName, value) {
      const updatedForm = {
        ...this.consentForm,
        [fieldName]: value
      }
      this.$emit('update:consentForm', updatedForm)
    },

    bulkUpdateConsentFields(updates) {
      const updatedForm = {
        ...this.consentForm,
        ...updates
      }
      this.$emit('update:consentForm', updatedForm)
    },

    updateEmailToSend(value) {
      this.$emit('update:emailToSend', value)
    },

    openConsentModal(row) {
      console.log("Opening Modal for row:", row);

      const referralReasons = (row.referralReasons || '');
      let detectedTemplates = [];

      if (referralReasons.includes('Endodontic Surgery')) {
        detectedTemplates.push('Endodontic Microsurgery');
      }

      if (referralReasons.includes('Restorability')) {
        detectedTemplates.push('Restorability Investigation');
      }

      if (referralReasons.includes('Vital Pulp Therapy')) {
        detectedTemplates.push('Vital Pulp Therapy');
      }

      if (referralReasons.includes('Endodontic Treatment')) {
        detectedTemplates.push('Endodontic Treatment');
      }

      // 2. Classifications Logic
      const isECAT = row.systemType === 0;
      const primaryPrefix = isECAT ? "E-CAT" : "R-CAT";
      const secondaryPrefix = isECAT ? "R-CAT" : "E-CAT";

      const pClass = `${primaryPrefix}: ${row.surveyClass || 'N/A'}`;
      const sClass = row.otherSurveyClass ? `${secondaryPrefix}: ${row.otherSurveyClass}` : '';

      // 3. Perform Bulk Update for Initial Data
      this.bulkUpdateConsentFields({
        patientName: row.patientName || '',
        patientEmail: row.patientEmail || '',
        classification: row.surveyClassNumber,
        primaryClassification: pClass,
        secondaryClassification: sClass,
        surveyId: row.surveyId
      });

      this.currentSurveyId = row.surveyId;

      // 4. Fetch Survey Data
      this.axios.get("/api/report/getbyid", {
        params: { surveyId: row.surveyId, },
        headers: {
          'Authorization': 'Bearer ' + this.user.token
        }
      }).then((response) => {
        var survey = response.data;
        this.survey = survey;

        this.applyTemplates(detectedTemplates, survey?.toothNumber || '');
      });

      this.updateShowConsentModal(true);
    },

    // New method to handle single template selection from dropdown
    applySelectedTemplate() {
      this.applyTemplates([this.selectedTemplateKey]);
    },

    // Core logic to merge multiple templates
    applyTemplates(keys, toothNumber) {
      if (!keys || keys.length === 0) return;

      this.selectedTemplateKey = keys[0];

      let combinedData = {
        procedure: [],
        briefExplanation: [],
        intendedBenefits: [],
        significantRisks: [],
        alternatives: []
      };

      keys.forEach(key => {
        const template = PROCEDURETEMPLATES[key];
        if (template) {
          combinedData.procedure.push(template.procedureLabel);
          combinedData.briefExplanation.push(template.briefExplanation || '');
          combinedData.intendedBenefits.push(template.intendedBenefits);
          combinedData.significantRisks.push(template.significantRisks);
          combinedData.alternatives.push(template.alternatives);
        }
      });

      const { complexities, specificRisks } = this.generateComplexitiesData();

      const updates = {
        procedure: combinedData.procedure.join(' + '),
        briefExplanation: [...new Set(combinedData.briefExplanation.filter(x => x))].join(' / '),
        intendedBenefits: this.combineTextLists(combinedData.intendedBenefits),
        significantRisks: this.combineTextLists(combinedData.significantRisks),
        alternatives: this.combineTextLists(combinedData.alternatives),
        complexities: complexities,
        specificRisks: specificRisks
      };

      // Include toothNumber if provided (from initial load)
      if (toothNumber !== undefined) {
        updates.toothNumber = toothNumber;
      }

      this.bulkUpdateConsentFields(updates);
    },

    // Helper to merge lists of bullet points and remove duplicates
    combineTextLists(textArrays) {
      let uniqueLines = new Set();
      let resultLines = [];

      textArrays.forEach(block => {
        if (!block) return;
        const lines = block.split('\n');
        lines.forEach(line => {
          const trimmed = line.trim();
          // Remove empty lines or just dashes
          if (trimmed && trimmed !== '-' && trimmed !== '•') {
            if (!uniqueLines.has(trimmed)) {
              uniqueLines.add(trimmed);
              resultLines.push(trimmed);
            }
          }
        });
      });
      return resultLines.join('\n');
    },

    generateComplexitiesData() {
      if (!this.survey || !this.survey.questions) {
        return { complexities: '', specificRisks: '' };
      }

      var complexityItems = [];

      this.survey.questions.forEach(question => {
        const significantResponses = question.responses.filter(response => response.weight > 0);
        if (significantResponses.length > 0) {
          const complexityEntry = question.responses[0].response;
          complexityItems.push(complexityEntry);
        }
      });

      let complexitiesFormatted = "";
      if (complexityItems.length > 0) {
        complexitiesFormatted = complexityItems.map(item => `- ${item}`).join('\n');
      }

      return {
        complexities: complexitiesFormatted,
        specificRisks: this.getComplexityRisks(complexityItems)
      };
    },

    getComplexityRisks(complexityFactors = []) {
      if (complexityFactors == null || complexityFactors.length == 0) return "";
      const risks = new Set();

      complexityFactors.forEach(factor => {
        const normalizedFactor = factor.toLowerCase();

        Object.keys(COMPLEXITY_RISKS_MAP).forEach(key => {
          if (normalizedFactor.includes(key) || key.includes(normalizedFactor)) {
            COMPLEXITY_RISKS_MAP[key].forEach(risk => risks.add(risk));
          }
        });
      });

      return Array.from(risks).map(risk => `- ${risk}`).join('\n');
    },

    // ----------------------------------------------------------------
    // PDF GENERATION LOGIC
    // ----------------------------------------------------------------
    generateConsentPdfDocument(consentData, survey) {
      if (!consentData || !consentData.patientName) {
        console.error("Cannot generate PDF: Missing required consent data.");
        return null;
      }

      const { patientName, procedure, briefExplanation, intendedBenefits, complexities, specificRisks, significantRisks, alternatives, primaryClassification, secondaryClassification } = consentData;

      const doc = new jsPDF({ unit: 'pt' });
      const marginLeft = 40;
      const marginRight = 40;
      const contentWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 80;
      let currentY = 40;

      const checkPageBreak = (additionalHeight = 50) => {
        if (currentY + additionalHeight > pageHeight - bottomMargin) {
          doc.addPage();
          currentY = 40;
          return true;
        }
        return false;
      };

      const drawUnderline = (x, y, width = 150) => {
        doc.line(x, y + 2, x + width, y + 2);
      };

      const drawFieldWithUnderline = (label, value, x, y, underlineWidth = 150) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const labelWidth = doc.getTextWidth(label);
        doc.text(label, x, y);

        if (value && value.trim() !== '') {
          doc.text(value, x + labelWidth + 5, y);
          const valueWidth = doc.getTextWidth(value);
          const remainingWidth = underlineWidth - valueWidth;
          if (remainingWidth > 10) {
            drawUnderline(x + labelWidth + 5 + valueWidth, y, remainingWidth);
          }
        } else {
          drawUnderline(x + labelWidth + 5, y, underlineWidth);
        }
      };

      const drawBoldFieldWithUnderline = (label, value, x, y, underlineWidth = 150) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        const labelWidth = doc.getTextWidth(label);
        doc.text(label, x, y);

        if (value && value.trim() !== '') {
          doc.text(value, x + labelWidth + 5, y);
          const valueWidth = doc.getTextWidth(value);
          const remainingWidth = underlineWidth - valueWidth;
          if (remainingWidth > 10) {
            drawUnderline(x + labelWidth + 5 + valueWidth, y, remainingWidth);
          }
        } else {
          drawUnderline(x + labelWidth + 5, y, underlineWidth);
        }
      };

      const drawTextAreaOrUnderlines = (content, x, startY, width, minLines = 2) => {
        if (content && content.trim() !== '') {
          const splitText = doc.splitTextToSize(content, width);
          const contentHeight = splitText.length * 12;
          if (checkPageBreak(contentHeight)) {
            startY = currentY;
          }
          doc.text(splitText, x, startY);
          return startY + (splitText.length * 12);
        } else {
          const underlinesHeight = minLines * 15;
          if (checkPageBreak(underlinesHeight)) {
            startY = currentY;
          }
          let currentLineY = startY;
          for (let i = 0; i < minLines; i++) {
            drawUnderline(x, currentLineY, width);
            currentLineY += 15;
          }
          return currentLineY;
        }
      };

      // Header
      const headerHeight = 60;
      const headerY = currentY;
      doc.setFillColor(43, 108, 176);
      doc.rect(marginLeft - 20, headerY - 15, contentWidth + 40, headerHeight, 'F');
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('CONSENT FORM FOR DENTAL TREATMENT', doc.internal.pageSize.getWidth() / 2, headerY + 15, { align: 'center' });
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Dental Assessment System', doc.internal.pageSize.getWidth() / 2, headerY + 32, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      currentY += headerHeight + 20;

      // Classification Section
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Assessment Classification:', marginLeft, currentY);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);

      // Draw Primary
      if (primaryClassification) {
        doc.text(primaryClassification, marginLeft + 160, currentY);
        currentY += 15;
      }

      // Draw Secondary (if exists)
      if (secondaryClassification) {
        doc.text(secondaryClassification, marginLeft + 160, currentY);
        currentY += 20;
      } else {
        currentY += 20;
      }

      // Procedure
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Name of proposed procedure or course of treatment', marginLeft, currentY);
      currentY += 20;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      currentY = drawTextAreaOrUnderlines(procedure, marginLeft, currentY, contentWidth - 100, 2);
      currentY += 30;

      // Tooth
      checkPageBreak(60);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Tooth or teeth to be treated', marginLeft, currentY);
      currentY += 20;
      const toothNumber = consentData.toothNumber || survey?.toothNumber || '';
      if (toothNumber && toothNumber.trim() !== '') {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(toothNumber, marginLeft, currentY);
      } else {
        drawUnderline(marginLeft, currentY, 200);
        drawUnderline(marginLeft + 220, currentY, 200);
        currentY += 15;
        drawUnderline(marginLeft, currentY, 200);
        drawUnderline(marginLeft + 220, currentY, 200);
      }
      currentY += 30;

      // Brief explanation
      checkPageBreak(80);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Brief explanation of medical term if not clear', marginLeft, currentY);
      currentY += 20;
      if (briefExplanation && briefExplanation.trim() !== '') {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        currentY = drawTextAreaOrUnderlines(briefExplanation, marginLeft, currentY, contentWidth, 2);
      } else {
        drawUnderline(marginLeft, currentY, 200);
        drawUnderline(marginLeft + 220, currentY, 200);
        currentY += 15;
        drawUnderline(marginLeft, currentY, 200);
        drawUnderline(marginLeft + 220, currentY, 200);
      }
      currentY += 30;

      // Anaesthesia
      checkPageBreak(100);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Type of Anaesthesia the Procedure will involve', marginLeft, currentY);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.text('(please tick as appropriate)', marginLeft + 300, currentY);
      currentY += 20;
      const checkboxSize = 8;
      const checkboxOptions = ['Local anaesthetic only', 'IV Sedation', 'Oral Sedation'];
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      checkboxOptions.forEach(option => {
        doc.rect(marginLeft, currentY - 6, checkboxSize, checkboxSize);
        doc.text(option, marginLeft + 15, currentY);
        currentY += 18;
      });
      currentY += 20;

      // Statement
      checkPageBreak(150);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Statement of Health professional', marginLeft, currentY);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.text('(to be filled in by health professional with appropriate knowledge of proposed procedure)', marginLeft, currentY + 15);
      currentY += 35;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('I have explained the procedure to the patient. In particular, I have explained:', marginLeft, currentY);
      currentY += 25;

      // Benefits
      checkPageBreak(100);
      doc.setFont('helvetica', 'bold');
      doc.text('The intended benefits', marginLeft, currentY);
      currentY += 15;
      doc.setFont('helvetica', 'normal');
      currentY = drawTextAreaOrUnderlines(intendedBenefits, marginLeft, currentY, contentWidth, 1);
      currentY += 15;

      // Complexities
      checkPageBreak(100);
      doc.setFont('helvetica', 'bold');
      doc.text('The following challenges or complexities have been identified for your case:', marginLeft, currentY);
      currentY += 15;
      doc.setFont('helvetica', 'normal');
      if (complexities && complexities.trim() !== '') {
        currentY = drawTextAreaOrUnderlines(complexities, marginLeft, currentY, contentWidth, 1);
      }
      if (survey?.furtherInformation && survey.furtherInformation.trim() !== '') {
        checkPageBreak(50);
        const splitInfo = doc.splitTextToSize(survey.furtherInformation, contentWidth);
        doc.text(splitInfo, marginLeft, currentY);
        currentY += (splitInfo.length * 12) + 10;
      }
      if ((!complexities || complexities.trim() === '') && (!survey?.furtherInformation || survey.furtherInformation.trim() === '')) {
        drawUnderline(marginLeft, currentY, contentWidth);
      }
      currentY += 25;

      // Specific risks
      checkPageBreak(100);
      doc.setFont('helvetica', 'bold');
      doc.text('These complexities bring the following specific risks:', marginLeft, currentY);
      currentY += 15;
      doc.setFont('helvetica', 'normal');
      currentY = drawTextAreaOrUnderlines(specificRisks, marginLeft, currentY, contentWidth, 1);
      currentY += 15;

      // Significant risks
      checkPageBreak(150);
      doc.setFont('helvetica', 'bold');
      doc.text('Significant, unavoidable or frequently occurring risks', marginLeft, currentY);
      currentY += 15;
      doc.setFont('helvetica', 'normal');
      doc.text('The following risks are generally associated with this procedure:', marginLeft, currentY);
      currentY += 15;
      if (significantRisks && significantRisks.trim() !== '') {
        currentY = drawTextAreaOrUnderlines(significantRisks, marginLeft, currentY, contentWidth, 2);
      } else {
        doc.text('•', marginLeft, currentY);
        drawUnderline(marginLeft + 15, currentY, contentWidth - 15);
        currentY += 15;
        doc.text('•', marginLeft, currentY);
        drawUnderline(marginLeft + 15, currentY, contentWidth - 15);
      }
      currentY += 25;

      // Alternatives
      checkPageBreak(100);
      doc.setFont('helvetica', 'bold');
      doc.text('Alternative treatment options:', marginLeft, currentY);
      currentY += 15;
      doc.setFont('helvetica', 'normal');
      if (alternatives && alternatives.trim() !== '') {
        currentY = drawTextAreaOrUnderlines(alternatives, marginLeft, currentY, contentWidth, 1);
      } else {
        doc.text('•', marginLeft, currentY);
        drawUnderline(marginLeft + 15, currentY, contentWidth - 15);
      }
      currentY += 25;

      // Footer stuff
      checkPageBreak(60);
      doc.setFont('helvetica', 'bold');
      doc.text('The Following Patient Information Leaflet Has Been Provided', marginLeft, currentY);
      currentY += 15;
      doc.setFont('helvetica', 'italic');
      drawUnderline(marginLeft, currentY, contentWidth);
      currentY += 25;

      checkPageBreak(60);
      doc.setFont('helvetica', 'bold');
      doc.text('I have also discussed what the procedure is likely to involve, the benefits and risks', marginLeft, currentY);
      currentY += 12;
      doc.text('of any available alternative treatments (including no treatment) and any particular concerns', marginLeft, currentY);
      currentY += 30;

      checkPageBreak(120);
      const fieldY = currentY;
      drawFieldWithUnderline('Health Professional\'s Signature ', '', marginLeft, fieldY, 180);
      drawFieldWithUnderline('Health Professional Name ', this.user?.name, marginLeft, fieldY + 20, this.user?.name ? 0 : 180);
      drawFieldWithUnderline('Job Title ', '', marginLeft, fieldY + 40, 180);
      drawFieldWithUnderline('GDC number ', '', marginLeft, fieldY + 60, 180);

      // Patient Page
      doc.addPage();
      currentY = 40;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Patient Agreement to Investigation / Treatment:', marginLeft, currentY);
      currentY += 25;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      const instructionText = 'Please read this form carefully. If you have any further questions, do ask - we are here to help you. You have the right to change your mind at any time, including after you have signed this form.';
      const splitInstruction = doc.splitTextToSize(instructionText, contentWidth);
      doc.text(splitInstruction, marginLeft, currentY);
      currentY += (splitInstruction.length * 12) + 20;

      doc.setFont('helvetica', 'normal');
      const agreementStatements = [
        'I agree to the procedure or course of treatment as described on this form.',
        'I understand that you cannot give me a guarantee that a particular person will perform the procedure. The person undertaking the procedure will however, have appropriate experience.',
        'I understand that any procedure in addition to those described on this form will only be carried out if it is necessary to save the life of the patient or to prevent serious harm to health.',
        'I have been told about additional procedures, including transfusions of blood and / or blood products, which may become necessary during treatment.',
      ];
      agreementStatements.forEach(statement => {
        checkPageBreak(50);
        const splitStatement = doc.splitTextToSize(statement, contentWidth);
        doc.text(splitStatement, marginLeft, currentY);
        currentY += (splitStatement.length * 12) + 15;
      });

      checkPageBreak(80);
      doc.text('I have listed below any procedures, which I do not wish to be carried out without further discussion. \nI fully understand the consequences of my decisions for myself/ my child.', marginLeft, currentY);
      currentY += 25;
      doc.setFont('helvetica', 'bold');
      doc.text('Procedures I do not wish to be carried out', marginLeft, currentY);
      currentY += 15;
      drawUnderline(marginLeft, currentY, contentWidth);
      currentY += 15;
      drawUnderline(marginLeft, currentY, contentWidth);
      currentY += 30;

      checkPageBreak(150);
      doc.setFont('helvetica', 'normal');
      doc.text('If you are signing this form on behalf of a child, please confirm the following:', marginLeft, currentY);
      currentY += 20;
      drawBoldFieldWithUnderline('Name of child: ', '', marginLeft, currentY, 200);
      currentY += 20;
      drawBoldFieldWithUnderline('Relationship to child: ', '', marginLeft, currentY, 200);
      currentY += 25;
      doc.rect(marginLeft, currentY - 6, checkboxSize, checkboxSize);
      doc.setFont('helvetica', 'bold');
      doc.text('I confirm that I have parental responsibility of the above-named child', marginLeft + 15, currentY);
      currentY += 30;

      checkPageBreak(80);
      drawBoldFieldWithUnderline('Patient/Parents Signature ', '', marginLeft, currentY, 200);
      currentY += 20;
      drawBoldFieldWithUnderline('Patient/Parents Name ', patientName, marginLeft, currentY, 200);
      currentY += 20;
      drawBoldFieldWithUnderline('Date Signed ', '', marginLeft, currentY, 200);

      return doc;
    },

    printConsentForm() {
      const { patientName, classification, procedure } = this.consentForm;
      const hasPatientName = patientName && patientName.toString().trim() !== '';
      const hasClassification = classification !== null && classification !== undefined && classification !== '';
      const hasProcedure = procedure && procedure.toString().trim() !== '';

      if (!hasPatientName || !hasClassification || !hasProcedure) {
        alert('Please fill in required fields (Patient Name, Classification, Procedure) to print.');
        return;
      }

      const doc = this.generateConsentPdfDocument(this.consentForm, this.survey);
      if (doc) {
        doc.save(`E-CAT-Consent-${patientName.toString().replace(/\s+/g, '-')}.pdf`);
      } else {
        alert("Could not generate the PDF due to missing information.");
      }
    },

    emailConsentForm() {
      if (!this.consentForm.patientEmail) {
        alert('Please enter a valid patient email address in the form');
        return;
      }
      this.updateEmailToSend(this.consentForm.patientEmail);
      this.updateShowEmailModal(true);
    },

    async confirmSendEmail() {
      this.isLoading = true;
      this.updateShowEmailModal(false);

      try {
        if (!this.consentForm.patientName || !this.consentForm.classification || !this.consentForm.procedure) {
          alert('Please ensure Patient Name, Classification, and Procedure are filled to send email.');
          this.isLoading = false;
          return;
        }

        if (!this.emailToSend || !/\S+@\S+\.\S+/.test(this.emailToSend)) {
          alert('Please enter a valid email format.');
          this.isLoading = false;
          return;
        }

        const doc = this.generateConsentPdfDocument(this.consentForm, this.survey);
        if (!doc) {
          throw new Error("PDF generation failed, cannot send email.");
        }

        const pdfDataUri = doc.output('dataurlstring');
        const pdfBase64 = pdfDataUri.split(',')[1];

        const payload = {
          surveyId: this.currentSurveyId,
          patientName: this.consentForm.patientName,
          patientEmail: this.emailToSend,
          procedure: this.consentForm.procedure,
          pdfBase64: pdfBase64
        };

        if (!this.user?.token) {
          throw new Error("Authentication token is missing. Please log in again.");
        }

        await this.axios.post("/api/survey/SendPreGeneratedConsentPdf", payload, {
          headers: { 'Authorization': 'Bearer ' + this.user.token }
        });

        alert('Consent form has been sent successfully.');
        this.updateShowConsentModal(false);

      } catch (error) {
        console.error('Error sending consent form:', error);
        let errorMsg = 'Failed to send consent form. Please try again.';
        if (error.response) {
          errorMsg = error.response.data?.message || error.response.data || errorMsg;
        } else if (error.message) {
          errorMsg = error.message;
        }
        alert(errorMsg);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* Keeping your existing styles exactly as they were */
::v-deep .modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 1.3rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: #fff;
  width: 90%;
  overflow-y: auto;
  max-height: 90%;
}

::v-deep .consent-modal-content {
  max-width: 900px;
  width: 90%;
}

::v-deep .email-modal-content {
  max-width: 400px;
  text-align: center;
}

.modal__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: transparent;
  border: 0px;
  outline: 0px;
  font-size: 32px;
}

.consent-title {
  font-size: 24px;
  margin-top: 0;
  margin-bottom: 25px;
  text-align: center;
  color: #111827;
}

.consent-form {
  .form-section {
    margin-bottom: 30px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #013B82;
      margin: 0 0 15px 0;
      padding-bottom: 8px;
      border-bottom: 2px solid #013B82;
    }

    /* Styles for the new Template Selection Section */
    &.template-section {
      background-color: #ebf8ff;
      border: 1px solid #bee3f8;

      .section-title {
        color: #2b6cb0;
        border-bottom-color: #2b6cb0;
      }
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 15px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .form-group {
    margin-bottom: 15px;
    text-align: left;

    &.full-width {
      grid-column: 1 / -1;
    }

    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: #374151;
      font-size: 14px;
    }

    .helper-text {
      color: #718096;
      font-size: 12px;
      margin-top: 4px;
      font-style: italic;
    }

    .readonly-input {
      background-color: #e9ecef;
      cursor: not-allowed;
      color: #495057;
    }

    .form-input,
    .form-textarea {
      width: 100%;
      padding: 10px 14px;
      border: 1px solid #D1D5DB;
      border-radius: 6px;
      font-size: 14px;
      transition: all 0.3s ease;
      font-family: inherit;

      &:focus {
        border-color: #013B82;
        box-shadow: 0 0 0 3px rgba(1, 59, 130, 0.1);
        outline: none;
      }
    }

    .form-textarea {
      resize: vertical;
      min-height: 80px;
    }
  }
}

.consent-actions,
.email-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;

  .btn-icon {
    flex-shrink: 0;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(1, 59, 130, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(1, 59, 130, 0.2);
  }
}

.btn-secondary {
  background: white;
  color: #4B5563;
  border: 1px solid #D1D5DB;

  &:hover {
    background: #F9FAFB;
    color: #111827;
    border-color: #9CA3AF;
  }

  &:active {
    background: #F3F4F6;
  }
}

.email-form-group {
  margin-bottom: 15px;
  text-align: left;

  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    color: #374151;
  }

  .form-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #D1D5DB;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.3s ease;

    &:focus {
      border-color: #013B82;
      box-shadow: 0 0 0 2px rgba(1, 59, 130, 0.1);
      outline: none;
    }
  }
}
</style>